/**
 * Utility functions for filtering N/A columns in Learning Outcome Reports
 */

import { ReportTreeNode } from '../../../types';

export interface ReportData {
  [userId: string]: {
    [itemId: string]: {
      score: number;
      max_score: number;
      attempts: number;
    };
  };
}

/**
 * Check if a specific column has N/A data for all students
 * Empty is defined as: max_score === 0 AND attempts === 0
 */
export const isColumnEmpty = (report: ReportData, columnId: string): boolean => {
  if (!report || Object.keys(report).length === 0) {
    return false;
  }

  const allStudentsHaveEmpty = Object.values(report).every((studentReport) => {
    const columnData = studentReport?.[columnId];
    return !columnData || (columnData.max_score === 0 && columnData.attempts === 0);
  });

  return allStudentsHaveEmpty;
};

/**
 * Get all leaf node IDs from a tree structure
 */
export const getAllLeafNodeIds = (node: ReportTreeNode): string[] => {
  if (!Array.isArray(node.subNodes) || node.subNodes.length === 0) {
    return [node.id];
  }

  return node.subNodes.flatMap((subNode) => getAllLeafNodeIds(subNode));
};

/**
 * Filter tree to remove N/A nodes
 * Recursively filters out nodes where all students have N/A values
 * If a parent node has all children filtered out, the parent is also removed
 */
export const filterTreeNodes = (
  node: ReportTreeNode | undefined,
  report: ReportData,
  hideEmpty: boolean,
): ReportTreeNode | null => {
  if (!hideEmpty || !node) {
    return node || null;
  }

  const isLeaf = !Array.isArray(node.subNodes) || node.subNodes.length === 0;

  if (isLeaf) {
    const isEmpty = isColumnEmpty(report, node.id);
    return isEmpty ? null : node;
  }

  const filteredChildren = node.subNodes
    .map((child) => filterTreeNodes(child, report, hideEmpty))
    .filter((child): child is ReportTreeNode => child !== null);

  if (filteredChildren.length === 0) {
    return null;
  }

  return {
    ...node,
    subNodes: filteredChildren,
  };
};

/**
 * Get all node IDs (including parents) from a tree structure that should be hidden
 * because all their descendants are N/A
 */
export const getColumnsToHide = (
  node: ReportTreeNode,
  report: ReportData,
  hideEmpty: boolean,
): string[] => {
  if (!hideEmpty || !node || !report) {
    return [];
  }

  const columnsToHide: string[] = [];

  const processNode = (currentNode: ReportTreeNode, path: string[] = []): boolean => {
    const currentPath = [...path, currentNode.id];
    const currentPathKey = currentPath.join('.');

    if (!Array.isArray(currentNode.subNodes) || currentNode.subNodes.length === 0) {
      const isEmpty = isColumnEmpty(report, currentNode.id);
      if (isEmpty) {
        columnsToHide.push(currentPathKey);
      }
      return isEmpty;
    }

    const childResults = currentNode.subNodes.map((subNode) =>
      processNode(subNode, currentPath),
    );

    const allChildrenEmpty = childResults.every((result) => result);
    if (allChildrenEmpty) {
      columnsToHide.push(currentPathKey);
    }

    return allChildrenEmpty;
  };

  processNode(node);
  return columnsToHide;
};
